import React from 'react';
import { FormattedHTMLMessage, InjectedIntl, injectIntl } from 'react-intl';
import {
    FieldLabel,
    FormFieldComponentProps,
    FormSelectChoice,
    InputSelectComponent,
    getAvailableStateChoices,
    Country,
    ProgramTheme,
    VerificationService,
    ExtendedFieldId,
    produceDraftViewModel,
    ProgramThemeMessages,
} from '@sheerid/jslib';
import { RegionMessages } from '../../lib/types/types';

export interface RegionSelectProps {
    verificationService: VerificationService;
    country: Country;
}

const defaultRegionConfiguration = {
    label: 'Region',
    placeholder: 'Region',
    options: [] as FormSelectChoice<string, string>[],
};

const getRegionFieldConfiguration = (
    programTheme: ProgramTheme,
    selectedCountry: Country,
    intl: InjectedIntl,
) => {
    if (!selectedCountry) return defaultRegionConfiguration;

    const programMessages = programTheme.intl.messages as ProgramThemeMessages & RegionMessages;
    const regionMessages = programMessages.custom?.region?.[selectedCountry];

    switch (selectedCountry) {
        case 'US':
            return {
                label: intl.formatHTMLMessage({ id: 'state', defaultMessage: 'State' }),
                placeholder: intl.formatHTMLMessage({ id: 'statePlaceholder', defaultMessage: 'State' }),
                options: getAvailableStateChoices(programTheme, intl),
            };
        default:
            if (!regionMessages) return defaultRegionConfiguration;
            return {
                label: regionMessages?.label,
                placeholder: regionMessages?.placeHolder,
                options: Object.keys(regionMessages?.options)?.map((option: string) => ({
                    value: option,
                    label: `${regionMessages.options[option]}`,
                })) || [],
            };
    }
};

export const RegionSelect = ({
    verificationService,
    country,
    value,
    isErrored,
    onChange,
    intl,
    isRequired = false,
}: FormFieldComponentProps & RegionSelectProps) => {
    const regionFieldConfiguration = getRegionFieldConfiguration(verificationService.programTheme, country, intl);

    const showAndValidateField = (): Boolean => {
        const fieldsToSkip: ExtendedFieldId[] = Object.assign([], verificationService.viewModel.fieldsToSkipValidation);
        if (regionFieldConfiguration.options.length === 0) {
            if (!fieldsToSkip.includes('region')) {
                fieldsToSkip.push('region');
                verificationService.updateViewModel(
                    produceDraftViewModel(verificationService.viewModel, 'fieldsToSkipValidation', fieldsToSkip),
                );
            }

            return false;
        }

        if (fieldsToSkip.includes('region')) {
            verificationService.updateViewModel(
                produceDraftViewModel(verificationService.viewModel, 'fieldsToSkipValidation', fieldsToSkip.filter((fieldId) => fieldId !== 'region')),
            );
        }
        return true;
    };

    const inputId = 'sid-region';

    return (
        (showAndValidateField())
        && (
            <div className={`sid-field ${inputId}`}>
                <div className="sid-l-space-top-md" />
                <FieldLabel
                    text={regionFieldConfiguration.label}
                    htmlForLabel={inputId}
                    id={`${inputId}-label`}
                    displayClasses={regionFieldConfiguration.placeholder && 'sid-h-screen-reader-only'}
                    isRequired={isRequired}
                />

                <InputSelectComponent
                    fieldId="region"
                    inputId={inputId}
                    fieldLabelId={`${inputId}-label`}
                    isErrored={isErrored}
                    options={regionFieldConfiguration.options}
                    onChange={(region: FormSelectChoice) => onChange(region)}
                    placeholder={regionFieldConfiguration.placeholder}
                    value={regionFieldConfiguration.options.find((region) => region.value === value)}
                />
                {isErrored ? (
                    <div className="sid-field-error">
                        <FormattedHTMLMessage id="errorId.invalidRegion" defaultMessage="{errorId.invalidRegion}" />
                    </div>
                ) : null}
            </div>
        )
    );
};

export const RegionSelectComponent = injectIntl(RegionSelect);
